import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import enClassroom from "@/locales/en/classroom.json";
import esClassroom from "@/locales/es/classroom.json";

import { readProfileLocaleFromUserInfo, resolveUiLanguage, type SupportedI18nLanguage } from "@/i18n/language";

export const i18nInstance = i18n.createInstance();

const CLASSROOM_NS = "classroom" as const;

const resources = {
	en: { [CLASSROOM_NS]: enClassroom },
	es: { [CLASSROOM_NS]: esClassroom },
};

/**
 * Initialise the app i18next instance (host). Call once before `ReactDOM.createRoot().render`.
 * Safe to call again in tests: reapplies language without re-running full plugin setup.
 */
export async function initI18nApp(): Promise<void> {
	const initial = resolveUiLanguage({});

	if (i18nInstance.isInitialized) {
		await i18nInstance.changeLanguage(initial.i18nLanguage);
		return;
	}

	await i18nInstance.use(initReactI18next).init({
		resources,
		ns: [CLASSROOM_NS],
		defaultNS: CLASSROOM_NS,
		lng: initial.i18nLanguage,
		fallbackLng: "en",
		interpolation: {
			escapeValue: false,
		},
		react: {
			useSuspense: false,
		},
	});
}

/**
 * Re-apply locale when account settings load or change (see {@link readProfileLocaleFromUserInfo}).
 */
export async function applyResolvedLanguageFromSession(accountSettings: unknown): Promise<void> {
	const profileLocale = readProfileLocaleFromUserInfo(accountSettings);
	const resolved = resolveUiLanguage({ profileLocale });
	await i18nInstance.changeLanguage(resolved.i18nLanguage);
}

export type { SupportedI18nLanguage };
export {
	ACCOUNT_LOCALE_EN,
	ACCOUNT_LOCALE_ES,
	normalizePreferredLocaleForSelect,
	normalizeToSupportedI18nLanguage,
	resolveUiLanguage,
} from "@/i18n/language";
